/* ReactionsFeed — compacte feed van laatste ouder-reacties met privé-berichtjes.
 * Bedoeld voor leidsters in de Groepsapp: één blik = wat thuis geliefd is. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Heart, ThumbsUp, MessageCircle } from "lucide-react";

const photoUrl = (url) => (url?.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const sec = Math.round((Date.now() - d.getTime()) / 1000);
    if (sec < 60) return "zojuist";
    if (sec < 3600) return `${Math.floor(sec / 60)}m`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}u`;
    if (sec < 604800) return `${Math.floor(sec / 86400)}d`;
    return d.toLocaleDateString("nl-NL", { day: "numeric", month: "short" });
  } catch { return ""; }
}

export default function ReactionsFeed({ limit = 8 }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    api.get("/photos/reactions/feed", { params: { limit } })
      .then(r => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [limit]);
  if (items === null) return null;
  if (items.length === 0) return null;
  return (
    <Card className="p-3 bg-white shadow-none" data-testid="reactions-feed">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-7 h-7 rounded-md flex items-center justify-center bg-red-100 text-red-600">
          <Heart className="h-4 w-4 fill-current" />
        </div>
        <div className="text-sm font-medium text-neutral-800">Laatste ouder-reacties</div>
        <div className="text-[10px] text-neutral-500 ml-auto">Wat thuis geliefd is</div>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-start gap-2 p-2 bg-neutral-50 rounded" data-testid={`reaction-item-${i}`}>
            <div className="shrink-0 w-10 h-10 rounded overflow-hidden bg-neutral-200">
              {it.photo_url ? (
                <img src={photoUrl(it.photo_url)} alt="" className="w-full h-full object-cover" />
              ) : null}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 text-xs">
                {it.emoji === "heart" ? (
                  <Heart className="h-3 w-3 text-red-500 fill-current" />
                ) : (
                  <ThumbsUp className="h-3 w-3 text-blue-500" />
                )}
                <span className="font-medium text-neutral-800 truncate">{it.parent_name || "Ouder"}</span>
                {it.kind_names?.length > 0 && (
                  <span className="text-neutral-500 truncate">· {it.kind_names.join(", ")}</span>
                )}
                <span className="text-[10px] text-neutral-400 ml-auto shrink-0">{timeAgo(it.reacted_at)}</span>
              </div>
              {it.message ? (
                <div className="text-xs text-neutral-700 italic mt-0.5 flex items-start gap-1">
                  <MessageCircle className="h-3 w-3 text-neutral-400 shrink-0 mt-0.5" />
                  <span className="line-clamp-2">"{it.message}"</span>
                </div>
              ) : (
                <div className="text-[11px] text-neutral-400 italic mt-0.5">Alleen een reactie, geen bericht</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
